import { Link } from 'react-router-dom'
import AccountLayout from '../../components/layout/account-layout'
import IconBox from '../../components/common/icon-box'
import Button from '../../components/common/button'
import { useUser } from '../../common/hooks/auth/useUser'
import { toast } from 'react-toastify'
import { SubmitHandler, useForm } from 'react-hook-form'
import { useMutation, useQuery } from '@tanstack/react-query'
import ControlledCheckbox from '../../components/form/controlled-checkbox'
import { useUserApi } from '../../common/api-context'
import { GermanLangKey, useTranslation } from '../../useTranslation'
import I18nText from '../../components/common/i18n-text'
import { SubscribeToGoldzeitenDe, SubscribeToHeimerleMeule } from '@ringo/shared'

type Form = SubscribeToHeimerleMeule & SubscribeToGoldzeitenDe

const Overview = () => {
    const { t, i18n } = useTranslation()
    const { user } = useUser()
    const userApi = useUserApi()

    const { data: subscription, refetch } = useQuery({
        queryKey: ['getUserSubscription', user?.id],
        queryFn: async () => {
            return (await userApi.isSubscribeToNewsletter()).data
        },
    })

    const { handleSubmit, control } = useForm<Form>({
        values: {
            subscribeToGoldzeitenDe: subscription?.subscribeToGoldzeitenDe,
            subscribeToHeimerleMeule: subscription?.subscribeToHeimerleMeule,
        },
    })

    const mutation = useMutation({
        mutationFn: async (formData: Form) => {
            var promises: Promise<any>[] = []
            if (subscription?.subscribeToGoldzeitenDe !== formData.subscribeToGoldzeitenDe) {
                promises.push(userApi.subscribeToGoldzeiten({ subscribeToGoldzeitenDe: formData.subscribeToGoldzeitenDe }))
            }
            if (subscription?.subscribeToHeimerleMeule !== formData.subscribeToHeimerleMeule) {
                promises.push(userApi.subscribeToHM({ subscribeToHeimerleMeule: formData.subscribeToHeimerleMeule }))
            }
            await Promise.all(promises)
        },
        onSuccess: () => {
            refetch()
            toast.success(t('myAccount.overview.youHaveSuccessfullyUpdatedYourNewsletterSubscription'))
        },
        onError: () => {
            toast.error(t('myAccount.overview.thereWasAnErrorUpdatingYourNewsletterSubscription'))
        },
    })

    const newsletterUpdate: SubmitHandler<Form> = (data) => {
        mutation.mutate(data)
    }

    return (
        <AccountLayout>
            <form onSubmit={handleSubmit(newsletterUpdate)}>
                <IconBox icon={'user'}>
                    <div className="pt-12 pb-6 px-2 max-tablet:pt-10">
                        <p className="font-bold text-xl text-left leading-none max-tablet:text-sm">
                            {t('hello')}, {user?.masterData?.firstName} {user?.masterData?.lastName}!
                        </p>
                        <p className="text-left mt-1.5 text-base text-black font-light max-tablet:text-sm">
                            {t('myAccount.overview.welcomeToYourAccountOverview')}
                        </p>
                        <div className="flex justify-between pt-6 pb-12 max-tablet:pt-5 max-tablet:pb-7">
                            <div className="w-1/5 text-base text-left max-tablet:text-sm">
                                <p className="text-custom_yellow">Name</p>
                                <p>{user?.masterData?.firstName + ' ' + user?.masterData?.lastName}</p>
                            </div>
                            <div className="w-1/5 text-base text-left max-tablet:text-sm">
                                <p className="text-custom_yellow">{t('emailAddress')}</p>
                                <p>{user?.email}</p>
                            </div>
                            <div className="w-1/5 text-base text-left max-tablet:text-sm">
                                <p className="text-custom_yellow ">{t('customerNumber')}</p>
                                <p>{user?.customerNumber}</p>
                            </div>
                        </div>
                        <div>
                            <Link to="/account/addresses">
                                <Button type="squareBlue" icon="gear" text={t('myAccount.overview.changePersonalData')} />
                            </Link>
                        </div>
                    </div>
                </IconBox>
                <div className="mt-12 max-tablet:mt-10">
                    <IconBox icon={'paper-plane'}>
                        <div className="pt-12 pb-6 px-2 max-tablet:pt-10">
                            <p className="font-bold text-xl text-left max-tablet:text-sm">{t('myAccount.overview.subscribeToOurNewsletter')}</p>
                            <div className="text-left mt-2">
                                <ControlledCheckbox control={control} label={t('myAccount.overview.subscribeToGoldzeitenDe')} name="subscribeToGoldzeitenDe" />
                            </div>
                            <div className="text-left mt-2">
                                <ControlledCheckbox
                                    control={control}
                                    label={t('myAccount.overview.subscribeToHeimerleMeule')}
                                    name="subscribeToHeimerleMeule"
                                />
                            </div>
                            <p className="text-left text-black mt-2 pl-7 text-base max-tablet:text-sm max-tablet:pl-6">
                                <I18nText i18nKey="myAccount.newsIHaveReadThePrivacyPolicy">
                                    I would like to receive the Heimerle + Meule newsletter. I can unsubscribe at any time. I have read the information about
                                    the newsletter in the
                                    <a
                                        href={
                                            i18n.language === GermanLangKey
                                                ? 'https://www.heimerle-meule.com/de/datenschutz'
                                                : 'https://www.heimerle-meule.com/data-protection'
                                        }
                                        className="text-custom_yellow  underline"
                                        target="_blank"
                                    >
                                        privacy policy
                                    </a>
                                    .
                                </I18nText>
                            </p>
                            <div className="mt-12 max-tablet:mt-7">
                                <Button type="squareBlue" icon="check" text={t('save')} isLoading={mutation.isPending} />
                            </div>
                        </div>
                    </IconBox>
                </div>
            </form>
        </AccountLayout>
    )
}

export default Overview
